import { useState, type ReactNode } from "react";
import { PublishedBadge } from "../atoms/Badge.tsx";
import { Button } from "../atoms/Button.tsx";
import { Switch } from "../atoms/Switch.tsx";

export interface Column<T> {
  header: string;
  render: (item: T) => ReactNode;
}

interface Props<T extends { id: string; published: boolean; sortOrder: number }> {
  items: T[];
  columns: Column<T>[];
  onEdit: (item: T) => void;
  onDelete: (item: T) => void;
  onTogglePublish: (item: T) => void;
  /** When provided, rows become draggable and this fires with the new order. */
  onReorder?: (ordered: T[]) => void;
}

/** Generic content table: extra columns + published badge, publish switch, order, edit/delete. */
export function DataTable<T extends { id: string; published: boolean; sortOrder: number }>({
  items,
  columns,
  onEdit,
  onDelete,
  onTogglePublish,
  onReorder,
}: Props<T>) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const draggable = !!onReorder;

  const drop = (to: number) => {
    if (dragIndex === null || dragIndex === to) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const next = items.slice();
    const [moved] = next.splice(dragIndex, 1);
    next.splice(to, 0, moved!);
    setDragIndex(null);
    setOverIndex(null);
    onReorder?.(next);
  };

  return (
    <div className="panel table-scroll">
      <table className="data">
        <thead>
          <tr>
            {draggable && <th aria-label="Reorder" />}
            {columns.map((c) => (
              <th key={c.header}>{c.header}</th>
            ))}
            <th>Status</th>
            <th>Order</th>
            <th style={{ textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr
              key={item.id}
              className={overIndex === index && dragIndex !== null ? "drag-over" : undefined}
              onDragOver={
                draggable
                  ? (e) => {
                      e.preventDefault();
                      setOverIndex(index);
                    }
                  : undefined
              }
              onDrop={draggable ? () => drop(index) : undefined}
            >
              {draggable && (
                <td
                  className="drag-handle"
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragEnd={() => {
                    setDragIndex(null);
                    setOverIndex(null);
                  }}
                  aria-label="Drag to reorder"
                  title="Drag to reorder"
                >
                  ⠿
                </td>
              )}
              {columns.map((c) => (
                <td key={c.header}>{c.render(item)}</td>
              ))}
              <td>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <PublishedBadge published={item.published} />
                  <Switch
                    checked={item.published}
                    onChange={() => onTogglePublish(item)}
                    label={item.published ? "on" : "off"}
                  />
                </div>
              </td>
              <td>{item.sortOrder}</td>
              <td>
                <div className="cell-actions">
                  <Button size="sm" onClick={() => onEdit(item)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => onDelete(item)}>
                    Delete
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
